<template>
  <div class="modal is-active">
    <div class="modal-background" @click="close()"></div>
    <div class="modal-card h-100">
      <p v-if="loading">Loading...</p>
      <section v-else class="modal-card-body d-block br-1">

        <header class="d-flex align-items-center pb-3 space-between">
          <h2 class="fs-8">Share this video</h2>
          <button class="delete" @click="close"></button>
        </header>

        <div class="tabs is-boxed mb-4">
          <ul>
            <li :class="{'is-active': !linkMode}">
              <a href="" @click.prevent="linkMode = !linkMode">People</a>
            </li>
            <li :class="{'is-active': linkMode}">
              <a href="" @click.prevent="linkMode = !linkMode">Link</a>
            </li>
          </ul>
        </div>

        <section v-if="!linkMode">
          <p>Select people or teams to share your video with from the dropdown. Start typing a name to filter the results.</p>
          <div class="d-flex align-items-center">
            <div class="w-100 mr-2">
              <v-select
                multiple
                class="mt-0"
                v-model="selected"
                label="value"
                :options="shareables">
                <!--- http://sagalbot.github.io/vue-select/docs/Advanced/Templating.html --->
                <template slot="option" slot-scope="option">
                  <span class="d-flex">
                    <StarIcon v-if="option.star" style="margin-right: .5rem;"/>
                    <b v-if="option.team">Group:</b> {{ option.value }}</span>
                </template>
              </v-select>
            </div>
            <button
              @click="shareVideo"
              type='button'
              class='button is-primary is-medium'
              :disabled="selected.length === 0"
              :class="{ 'is-loading': sharingWithSelected }"
              name='Share'>
              Share
            </button>
          </div>
        </section>

        <section v-if="linkMode" class="d-flex flex-column align-items-center">
          <div class="field has-addons w-100" >
            <div class="control w-100">
              <input
                ref="share"
                class="input is-medium"
                :disabled="!slugShareEnabled"
                :value="shareURL"
                readonly/>
            </div>
            <div class="control">
              <button
                class="button is-primary is-medium"
                :disabled="!slugShareEnabled"
                @click="copyLink">
                Copy Link
              </button>
            </div>
          </div>
          <button
            v-if="!slugShareEnabled"
            @click="enableSlugSharing"
            :class="{ 'is-loading': enablingSlugSharing }"
            class='control button is-primary is-medium is-outlined'>
            Enable link sharing
          </button>
          <button
            v-if="slugShareEnabled"
            @click="disableSlugSharing()"
            :class="{ 'is-loading': disablingSlugSharing }"
            class='control button is-danger is-medium is-outlined'>
            Disable link sharing
          </button>
          <p class="text-center mt-2">
            <b>Note:</b> Anyone in your group who has this link can see your video.
          </p>
        </section>

        <section v-if="!linkMode">
          <hr/>
          <span v-if="showShareText" class="text-center text-italic">
            <p v-if="slugShareEnabled">This video has link sharing enabled</p>
            <p v-else>This video isn't shared with any person or team.</p>
          </span>
          <h1 v-else class="fs-6 text-center">Shared With:</h1>
          <ul class="overflow-auto mh-35">
            <li v-for="vs in video.shares" class="mb-1 d-flex justify-content-between">
              {{ vs.user.name || vs.user.email }}
               <input v-if='vs.creator == userId'
                  @click="unshareVideo(vs.id)"
                  type='button'
                  :class="{ 'is-loading': vs.unsharing, 'w-auto': true }"
                  class='button is-small is-danger is-outlined'
                  value='✕ Unshare'
                />
            </li>
            <li v-for="ts in video.teamshares" class="mb-1 d-flex justify-content-between">
               <div><b>Group:</b> {{ ts.team.name }}</div>
               <input v-if='ts.creator == userId'
                  @click="unshareTeamVideo(ts.id)"
                  type='button'
                  :class="{ 'is-loading': ts.unsharing, 'w-auto': true }"
                  class='button is-small is-danger is-outlined'
                  value='✕ Unshare'
                />
            </li>
          </ul>
        </section>

      </section>
    </div>
  </div>
</template>

<script>
import store from '../store.js'
import vSelect from 'vue-select'
import StarIcon from './StarIcon.vue'

export default {
  name: 'ShareModal',
  props: {
    video: {
      required: true,
    },
  },
  components: {
    'v-select': vSelect,
    StarIcon,
  },
  data () {
    return {
      selected: [],
      linkMode: false,
      enablingSlugSharing: false,
      disablingSlugSharing: false,
      sharingWithSelected: false,
      loading: true,
    }
  },
  async mounted () {
    document.addEventListener('keyup', this.escapeKeyPress)
    await store.dispatch('fetchGroup')
    this.loading = false
  },
  beforeDestroy () {
    document.removeEventListener('keyup', this.escapeKeyPress)
  },
  computed: {
    shareables () {
      // const shareUsers = store.state.group.use_colleagues ? store.state.user.colleagues : store.state.group.users
      const shareUsers = store.state.group.users
      return [
        ...store.state.user.teams
           .filter(team => this.video.teamshares != null && this.video.teamshares.find(x => x.team.id === team.id) == null)
           .map(team => ({
             value: team.name,
             label: 't-' + team.id,
             team: true,
           })),
        ...shareUsers
          .filter(user => this.video.shares != null && this.video.shares.find(x => x.user.id === user.id) == null)
          .filter(x => x.id !== store.state.user.id)
          .filter(x => !x.archived)
          .map(groupUser => ({
            value: groupUser.name || groupUser.email,
            label: 'u-' + groupUser.id,
            star: store.state.user.colleagues.find(x => x.id === groupUser.id),
          }))
          .sort(x => x.star ? -1 : 1)
      ]
    },
    slugShareEnabled () {
      return this.video.slug_share_enabled
    },
    showShareText () {
      return !this.linkMode && this.video.shares.length === 0 && this.video.teamshares.length === 0
    },
    shareURL () {
      return window.location.origin + `/v/${this.video.slug}`
    },
    userId () {
      return store.state.user.id
    }
  },
  methods: {
    escapeKeyPress (event) {
      if (event.keyCode === 27) {
        this.close()
      }
    },
    async enableSlugSharing () {
      this.enablingSlugSharing = true
      await store.dispatch('updateVideoSlugSharing', { videoID: this.video.id, val: true })
      this.enablingSlugSharing = false
    },
    async disableSlugSharing () {
      this.disablingSlugSharing = true
      await store.dispatch('updateVideoSlugSharing', { videoID: this.video.id, val: false })
      this.disablingSlugSharing = false
    },
    close () {
      this.$emit('close')
    },
    async shareVideo () {
      this.sharingWithSelected = true
      await Promise.all(this.selected.map(x => {
        const [ type, id ] = x.label.split('-')
        if (type === 't') {
          return store.dispatch('shareVideoWithTeam', { videoID: this.video.id, teamID: id })
        } else {
          return store.dispatch('shareVideoWithUser', { videoID: this.video.id, userID: id })
        }
      }))
      this.sharingWithSelected = false
      this.selected = []
    },
    unshareVideo (videoShareID) {
      store.dispatch('unshareVideoWithUser', { videoID: this.video.id, videoShareID })
    },
    unshareTeamVideo (teamShareID) {
      store.dispatch('unshareVideoWithTeam', { videoID: this.video.id, teamShareID })
    },
    copyLink () {
      this.$refs.share.select()
      document.execCommand('copy')
      store.commit('notify', { message: 'Link copied to clipboard!', level: 'primary' })
    },
  },
}

</script>

<style lang="scss" scoped>

.w-auto{
  width: auto;
}

</style>

<style lang="scss">

.v-select.dropdown {
  width: 100%;
}

ul.dropdown-menu {
  max-width: 509px !important;
  min-width: 509px !important;
}

.v-select .dropdown-toggle {
  width: 100%;
}

ul.dropdown-menu > li {
  width: 100%;
}

.dropdown-menu::-webkit-scrollbar {
    -webkit-appearance: none;
}

.dropdown-menu::-webkit-scrollbar:vertical {
    width: 11px;
}

.dropdown-menu::-webkit-scrollbar:horizontal {
    height: 11px;
}

.dropdown-menu::-webkit-scrollbar-thumb {
    border-radius: 8px;
    border: 2px solid white; /* should match background, can't be transparent */
    background-color: rgba(0, 0, 0, .5);
}

</style>



// WEBPACK FOOTER //
// ShareModal.vue?3cf602da